import { useEffect, useState } from "react";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { WorkbenchSelector } from "~/components/assistant/messages/export/WorkbenchSelector";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import type { ArtifactType, ConvertAndCopyFormat } from "~/services/artifacts";
import { defaultFormatFor, formatOptionsFor } from "./formats";
import { useArtifactActions } from "./useArtifactActions";

type Stage = "selectWorkbench" | "selectFormat" | "exporting" | "success" | "error";

interface WorkbenchOption {
  id: number;
  title: string;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  artifactId: string;
  artifactType: ArtifactType;
}

// Modal flow for exporting an artifact to an RWB workbench project.
// Mirrors the assistant-ui ExportDialog: pick a workbench (or create
// one) → pick a format → ship it. Workbench projects (RWB integer ids)
// are a separate identifier space from AI projects, so the workbench
// is always chosen at export time, never inherited from context.
export const ExportDialog = ({ open, onClose, artifactId, artifactType }: ExportDialogProps) => {
  const actions = useArtifactActions(artifactId);
  const options = formatOptionsFor(artifactType);
  const [stage, setStage] = useState<Stage>("selectWorkbench");
  const [workbench, setWorkbench] = useState<WorkbenchOption | null>(null);
  const [format, setFormat] = useState<ConvertAndCopyFormat>(defaultFormatFor(artifactType));
  const [error, setError] = useState<string | undefined>();
  const [exportedFileName, setExportedFileName] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setStage("selectWorkbench");
      setWorkbench(null);
      setFormat(defaultFormatFor(artifactType));
      setError(undefined);
      setExportedFileName(undefined);
    }
  }, [open, artifactType]);

  const handleWorkbenchSelected = (w: WorkbenchOption) => {
    setWorkbench(w);
    setStage("selectFormat");
  };

  const handleExport = async () => {
    if (!workbench) return;
    setStage("exporting");
    const result = await actions.exportToWorkbench(format, workbench.id);
    if (result.ok) {
      setExportedFileName(result.result.file_name);
      setStage("success");
    } else {
      setError(result.error);
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("selectFormat");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "selectWorkbench" && (
          <>
            <DialogHeader>
              <DialogTitle>Export to workbench</DialogTitle>
            </DialogHeader>
            <WorkbenchSelector onSelect={handleWorkbenchSelected} onCancel={onClose} />
          </>
        )}

        {stage === "selectFormat" && (
          <>
            <DialogHeader>
              <DialogTitle>Choose export format</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="mb-4 text-sm text-gray-600">
                Exporting to <span className="font-semibold">{workbench?.title}</span>
              </p>
              <RadioGroup
                value={format}
                onValueChange={(v) => setFormat(v as ConvertAndCopyFormat)}
              >
                {options.map((opt) => (
                  <RadioGroupItem
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    variant="card"
                    className="w-full"
                  />
                ))}
              </RadioGroup>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStage("selectWorkbench")}>
                Back
              </Button>
              <Button onClick={handleExport}>Export</Button>
            </div>
          </>
        )}

        {stage === "exporting" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Loader2 className="size-10 animate-spin text-primary" />
            <p className="text-base font-medium">Exporting…</p>
          </div>
        )}

        {stage === "success" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="rounded-full bg-green-100 p-5">
              <Check className="size-8 text-green-600" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-base">
                Your artifact has been sent to the workbench{" "}
                <span className="font-bold">{workbench?.title}</span>
              </p>
              <p className="text-sm text-gray-600">It may take a few minutes to appear.</p>
            </div>
            {exportedFileName && (
              <div className="mt-1 text-center">
                <p className="text-sm font-semibold">File name</p>
                <p className="font-mono text-sm text-gray-600">{exportedFileName}</p>
              </div>
            )}
            <div className="mt-2">
              <Button variant="link" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {stage === "error" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="rounded-full bg-red-100 p-5">
              <AlertCircle className="size-8 text-red-600" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-lg font-bold">Unable to export artifact</p>
              <p className="text-sm text-gray-600">
                {error || "Something went wrong while exporting your file."}
              </p>
            </div>
            <div className="mt-2 flex flex-col items-center gap-1">
              <Button onClick={handleRetry} className="px-6">
                Retry
              </Button>
              <Button variant="link" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
